import { useMemo } from "react";
import { Button, Popconfirm } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Link } from "react-router-dom";
import { IconTrash } from "@tabler/icons-react";
import { TableHeaderType, FilterType } from "../../components/tables/types";
import { CopyIconButton } from "../../components/copyButton";
import { DbtDocsShare, DbtDocsFilters } from "./types";

interface UseDbtDocsTableHeadersParams {
  filters: DbtDocsFilters;
  nameFilter: string[];
  setNameFilter: (value: string[]) => void;
  ownerFilter: string[];
  setOwnerFilter: (value: string[]) => void;
  shareAppURLs: Record<string, string>;
  onDelete: (shareId: string) => void;
}

export const useDbtDocsTableHeaders = ({
  filters,
  nameFilter,
  setNameFilter,
  ownerFilter,
  setOwnerFilter,
  shareAppURLs,
  onDelete,
}: UseDbtDocsTableHeadersParams): TableHeaderType<DbtDocsShare>[] => {
  return useMemo(
    () => [
      {
        id: 1,
        label: "Name of Share",
        uid: "name",
        width: "29%",
        filter: {
          filterType: FilterType.DROPDOWN,
          value: nameFilter,
          options: filters.names.map(name => ({ label: name, value: name })),
          onChange: (value: string[]) => setNameFilter(value),
        },
        render: (item: DbtDocsShare) => (
          <Link className="al-break-words" to={`/collaboration/dbt-docs/${item.share_id}`}>
            {item.name}
          </Link>
        ),
      },
      {
        id: 2,
        label: "Owner",
        uid: "owner",
        width: "18%",
        filter: {
          filterType: FilterType.DROPDOWN,
          value: ownerFilter,
          options: filters.owners.map(owner => ({ label: owner.email, value: String(owner.id) })),
          onChange: (value: string[]) => setOwnerFilter(value),
        },
        render: (item: DbtDocsShare) => <div className="al-break-words">{item.user.email}</div>,
      },
      {
        id: 3,
        label: "Conversations",
        uid: "conversations",
        width: "14%",
        render: (item: DbtDocsShare) => (
          <div className="al-break-words">
            <Stack align="center">
              <div>{item.open_conversations + item.resolved_conversations}</div>
              <div className="al-rounded-lg al-px-1 al-py-0.5 al-bg-bg-orange">
                <span className="al-text-action-orange al-text-center al-text-[10px]">{`${item.open_conversations} Open`}</span>
              </div>
            </Stack>
          </div>
        ),
      },
      {
        id: 4,
        label: "Notes",
        uid: "notes",
        width: "14%",
        render: (item: DbtDocsShare) => <div className="al-break-words">{item.description}</div>,
      },
      {
        id: 5,
        label: "Created",
        uid: "created_at",
        width: "14%",
        sort: {
          sortSearchKey: "sortAttribute",
        },
        render: (item: DbtDocsShare) => <div className="al-break-words">{item.created_at}</div>,
      },
      {
        id: 6,
        label: "Action",
        uid: "action",
        width: "11%",
        render: (item: DbtDocsShare) => (
          <Stack align="center">
            <Stack align="center">
              <div className="al-text-primary">URL</div>
              <CopyIconButton
                value={shareAppURLs[item.share_id] ?? ""}
                colorClassName="al-text-primary"
              />
            </Stack>
            <Popconfirm
              title="Are you sure?"
              description={`Are you sure you want to delete ${item.name}?`}
              okText="Delete"
              cancelText="Cancel"
              onConfirm={e => {
                e.stopPropagation();
                onDelete(item.share_id);
              }}
            >
              <Button variant="ghost" size="icon" title="Delete share">
                <IconTrash size={16} />
              </Button>
            </Popconfirm>
          </Stack>
        ),
      },
    ],
    [filters, nameFilter, setNameFilter, ownerFilter, setOwnerFilter, shareAppURLs, onDelete]
  );
};
